import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FolderOpen, FileText, Calendar, ChevronRight } from 'lucide-react';
import { getProjects } from '../../../services/api';
import { formatDistanceToNow } from 'date-fns';
import { useRefresh } from '../BlockCanvas';

const ProjectsBlock = ({ user, size = 'medium', refreshTrigger, onOpenProjectCreationModal }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { openProjectCreationModal } = useRefresh();

  useEffect(() => {
    loadProjects();
  }, [user, refreshTrigger]);

  const loadProjects = async () => {
    if (!user) return;
    
    try {
      const projectsData = await getProjects(user.id);
      setProjects(projectsData);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = () => {
    openProjectCreationModal();
  };

  const getDisplayCount = () => {
    switch (size) {
      case 'small':
        return 4;
      case 'medium':
        return 6;
      case 'large':
        return 12;
      default:
        return 6;
    }
  };

  const displayProjects = projects.slice(0, getDisplayCount());

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center h-3/4 text-center ${
        size === 'small' ? 'px-2' : 'px-4'
      }`}>
        <FolderOpen className={`text-gray-400 dark:text-gray-500 mb-2 ${
          size === 'small' ? 'h-8 w-8' : 'h-8 w-8'
        }`} />
        <p className={`text-gray-600 dark:text-gray-400 mb-3 ${
          size === 'small' ? 'text-xs' : 'text-sm'
        }`}>
          No projects yet
        </p>
        <button 
          onClick={handleCreateProject}
          className={`text-celadon-600 hover:text-celadon-700 dark:text-celadon-400 dark:hover:text-celadon-300 transition-colors font-medium ${
            size === 'small' ? 'text-xs' : 'text-xs'
          }`}
        >
          Create your first project
        </button>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {projects.length} Project{projects.length !== 1 ? 's' : ''}
          </span>
        </div>
        {projects.length > getDisplayCount() && (
          <Link 
            to="/dashboard"
            className="text-xs text-celadon-600 hover:text-celadon-700 dark:text-celadon-400 dark:hover:text-celadon-300 font-medium transition-colors"
          >
            View all
          </Link>
        )}
      </div>

      <div className="space-y-3 overflow-y-auto max-h-full custom-scrollbar">
        {displayProjects.map((project) => (
          <Link
            key={project.id}
            to={`/projects/${project.id}`}
            className="block p-4 bg-gray-50/80 hover:bg-gray-100/80 dark:bg-gray-700/50 dark:hover:bg-gray-600/50 rounded-2xl transition-all duration-200 group border border-gray-100/50 hover:border-gray-200/80 dark:border-gray-600/50 dark:hover:border-gray-500/80 hover:shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_2px_12px_-4px_rgba(0,0,0,0.3)]"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-celadon-700 dark:group-hover:text-celadon-300 transition-colors truncate mb-1">
                  {project.name}
                </h4>
                {project.description && size !== 'small' && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-1">
                    {project.description}
                  </p>
                )}
                <div className="flex items-center gap-4 mt-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center bg-gray-100/60 dark:bg-gray-600/60 px-2 py-1 rounded-lg">
                    <FileText className="h-3 w-3 mr-1.5" />
                    {project.prompt_count} prompt{project.prompt_count !== '1' ? 's' : ''}
                  </span>
                  {size !== 'small' && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                      <Calendar className="h-3 w-3 mr-1.5" />
                      {formatDistanceToNow(new Date(project.created_at), { addSuffix: true })}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500 group-hover:text-celadon-600 dark:group-hover:text-celadon-400 transition-colors ml-3 flex-shrink-0" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default ProjectsBlock; 